import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sales/public`;

const HERO = {
  fit: { h: 'Dein Fitnessstudio – digital.', s: 'Online-Buchung, eigene App, Kurse & Abos. Sieh in 2 Minuten, wie es für dich aussieht.' },
  care: { h: 'Deine Praxis – digital.', s: 'Verordnungen, Terminbuchung und Abrechnung aus einer Hand. Sieh deine persönliche Vorschau.' },
  dance: { h: 'Deine Tanzschule – digital.', s: 'Kurse, Probelektionen und Guthaben online. Sieh in 2 Minuten deine Vorschau.' },
  beauty: { h: 'Dein Beauty-Studio – digital.', s: 'Online-Buchung mit Wunschperson, Anzahlung und Kartei. Sieh deine Vorschau.' },
  dental: { h: 'Deine Zahnarztpraxis – digital.', s: 'Online-Termine, digitale Anamnese und Recall per WhatsApp. Sieh deine Vorschau.' },
  mobility: { h: 'Deine Garage – digital.', s: 'Termine, Fahrzeugakte und Freigaben per WhatsApp. Sieh deine Vorschau.' },
};

export default function SalesLanding() {
  const { branch = 'fit' } = useParams();
  const [sp] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', website: '', logo_url: '', consent: false, whatsapp_ok: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const captchaRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/config`, { params: { branch } }); setCfg(data); } catch { setCfg({ branch, branch_label: 'Invicta', recaptcha_enabled: false }); }
  }, [branch]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (cfg?.recaptcha_enabled && cfg.recaptcha_site_key && !window.grecaptcha) {
      const s = document.createElement('script');
      s.src = 'https://www.google.com/recaptcha/api.js'; s.async = true; document.body.appendChild(s);
    }
  }, [cfg]);

  const hero = HERO[branch] || HERO.fit;

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!form.company_name.trim() || !form.email.trim()) { setErr('Bitte Firma und E-Mail ausfüllen.'); return; }
    if (!form.consent) { setErr('Bitte stimme dem Erhalt der Vorschau zu.'); return; }
    let token = '';
    if (cfg?.recaptcha_enabled && window.grecaptcha) { try { token = window.grecaptcha.getResponse(); } catch {} if (!token) { setErr('Bitte die Sicherheitsprüfung bestätigen.'); return; } }
    setSending(true);
    try {
      const utm = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(k => { if (sp.get(k)) utm[k] = sp.get(k); });
      const { data } = await axios.post(`${API}/landing-submit`, {
        ...form, branch, recaptcha_token: token, base_url: window.location.origin, utm,
      });
      setDone(data);
    } catch (e2) { setErr(e2.response?.data?.detail || 'Es ist ein Fehler aufgetreten.'); }
    finally { setSending(false); }
  };

  const inp = 'w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] outline-none focus:border-teal-600';

  return (
    <div data-testid="sales-landing" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
      className="min-h-screen bg-gradient-to-b from-[#0e2b2a] to-[#15403e] text-white">
      <div className="mx-auto max-w-5xl px-5 py-10 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-xs tracking-wide">{cfg?.branch_label || 'Invicta'}</span>
          <h1 className="mt-5 text-4xl lg:text-5xl font-extrabold leading-tight">{hero.h}</h1>
          <p className="mt-4 text-lg text-white/80 max-w-md">{hero.s}</p>
          <ul className="mt-6 space-y-2 text-white/85 text-[15px]">
            <li>✓ Kostenlose Vorschau in deinem Design</li>
            <li>✓ Unverbindlich · 30 Tage gratis testen</li>
            <li>✓ Datenübernahme inklusive</li>
          </ul>
        </div>

        <div className="rounded-2xl bg-white text-slate-800 p-6 shadow-xl">
          {done ? (
            <div data-testid="sales-landing-done" className="text-center py-6">
              <div className="text-3xl mb-2">📧</div>
              <h2 className="text-xl font-bold text-[#15403e]">Fast geschafft!</h2>
              <p className="mt-2 text-sm text-slate-600">Wir haben dir eine E-Mail geschickt. Bitte bestätige deine Adresse – danach erstellen wir deine persönliche Vorschau.</p>
              {done.confirm_url && (
                <a href={done.confirm_url} data-testid="sales-landing-confirm-link" className="mt-4 inline-block text-teal-700 underline text-sm">E-Mail jetzt bestätigen</a>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <h2 className="text-lg font-bold text-[#15403e]">Kostenlose Vorschau anfordern</h2>
              <input data-testid="landing-company" className={inp} placeholder="Firma *" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} />
              <input data-testid="landing-name" className={inp} placeholder="Dein Name" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} />
              <input data-testid="landing-email" className={inp} type="email" placeholder="E-Mail *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <input data-testid="landing-phone" className={inp} placeholder="Handy" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              <input data-testid="landing-website" className={inp} placeholder="Website (empfohlen)" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="landing-consent" className="mt-0.5" checked={form.consent} onChange={e => setForm({ ...form, consent: e.target.checked })} />
                Ich möchte eine kostenlose, unverbindliche Vorschau erhalten und stimme der Kontaktaufnahme per E-Mail zu.
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="landing-whatsapp" className="mt-0.5" checked={form.whatsapp_ok} onChange={e => setForm({ ...form, whatsapp_ok: e.target.checked })} />
                Sie dürfen mich per WhatsApp kontaktieren. (optional)
              </label>
              {cfg?.recaptcha_enabled && cfg.recaptcha_site_key && (
                <div ref={captchaRef} className="g-recaptcha" data-sitekey={cfg.recaptcha_site_key}></div>
              )}
              {err && <p className="text-sm text-rose-600" data-testid="landing-error">{err}</p>}
              <button type="submit" disabled={sending} data-testid="landing-submit"
                className="w-full rounded-xl bg-[#15403e] py-3 text-white font-semibold hover:bg-[#1b524f] disabled:opacity-60">
                {sending ? 'Senden…' : 'Vorschau anfordern'}
              </button>
              <p className="text-[11px] text-slate-400 text-center">Double-Opt-in · jederzeit abmeldbar · kein Spam</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
